import React, { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import TableOfContents from '../components/TableOfContents';

import { blogPostsData } from './blog-posts';

function BlogPost() {
  const { slug } = useParams();
  const post = blogPostsData[slug];
  const contentRef = useRef(null);

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-charcoal mb-4">Post Not Found</h1>
          <p className="text-gray-500 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-sage-600 hover:text-sage-700 font-medium">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.title} | FoodLens AI Blog</title>
        <meta name="description" content={post.excerpt || `${post.title} - Discover insights about ${post.category.toLowerCase()} with FoodLens AI. Expert analysis and practical tips for healthier eating.`} />
        <meta name="keywords" content={post.keywords || `AI nutrition apps, food scanning, ingredient analysis, ${post.category.toLowerCase()}, FoodLens AI, healthy eating, nutrition technology`} />
        <meta name="author" content={post.author} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || `${post.title} - Discover insights about ${post.category.toLowerCase()} with FoodLens AI. Expert analysis and practical tips for healthier eating.`} />
        <meta property="og:image" content={`https://foodlens-ai.com${post.image}`} />
        <meta property="og:url" content={`https://foodlens-ai.com/blog/${slug}`} />
        <meta property="og:site_name" content="FoodLens AI" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || `${post.title} - Discover insights about ${post.category.toLowerCase()} with FoodLens AI. Expert analysis and practical tips for healthier eating.`} />
        <meta name="twitter:image" content={`https://foodlens-ai.com${post.image}`} />

        {/* Article specific meta tags */}
        <meta property="article:published_time" content={new Date(post.date).toISOString()} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://foodlens-ai.com/blog/${slug}`} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-8">
          {/* Table of Contents Sidebar */}
          <div className="hidden lg:block">
            <TableOfContents contentRef={contentRef} />
          </div>

          {/* Main Article Content */}
          <article className="flex-1 max-w-4xl">
            <div className="mb-8">
              <Link to="/blog" className="text-sage-600 hover:text-sage-700 font-medium inline-flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Blog
              </Link>
            </div>

            <header className="mb-8">
              <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4 gap-2">
                <span className="bg-sage-100 text-sage-700 px-3 py-1 rounded-lg text-xs font-medium">
                  {post.category}
                </span>
                <span>•</span>
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
                <span>•</span>
                <span>{post.readTime}</span>
                <span>•</span>
                <span>By {post.author}</span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl text-charcoal mb-6">
                {post.title}
              </h1>

              <div className="h-64 sm:h-96 bg-sage-100 rounded-2xl overflow-hidden mb-8">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-sage-100 to-sage-200 flex items-center justify-center" style={{display: 'none'}}>
                  <span className="text-sage-600 font-semibold text-xl">{post.category}</span>
                </div>
              </div>
            </header>

            {/* Mobile Table of Contents */}
            <div className="lg:hidden mb-8">
              <div className="w-full">
                <TableOfContents contentRef={contentRef} />
              </div>
            </div>

            <div ref={contentRef} className="prose prose-sage max-w-none">
              {post.content()}
            </div>

            <div className="mt-12 border-t border-sage-100 pt-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h4 className="text-lg font-semibold text-charcoal mb-3">Share this article</h4>
                  <div className="flex space-x-4">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-sage-600 transition-colors"
                    >
                      Twitter
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-sage-600 transition-colors"
                    >
                      Facebook
                    </a>
                    <a
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-sage-600 transition-colors"
                    >
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-12 bg-gradient-sage rounded-2xl p-8 text-center text-white">
              <h3 className="font-display text-2xl mb-3">Stop reading labels manually</h3>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                FoodLens instantly scans ingredients and flags preservatives, seed oils, allergens, and more while you shop.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://chromewebstore.google.com/detail/foodlens-ingredient-scann/dggilpddmoelgefehponacgieklibncd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  Chrome Extension (Free)
                </a>
                <Link
                  to="/app"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/20 text-white font-medium rounded-xl hover:bg-white/30 transition-colors border border-white/30"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  iOS App (Free)
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
